<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { useNotification } from '@kyvg/vue3-notification'
import { VueTelInput } from 'vue-tel-input'
import 'vue-tel-input/vue-tel-input.css'
import UserService from '@/services/UserService'
import FormButton from '@/components/Atoms/FormButton.vue'
import Alert from '@/components/Atoms/Alert.vue'
import Spinner from '@/components/ui/Spinner.vue'
import TurnstileWidget from '@/components/ui/TurnstileWidget.vue'
import OtpInput from '@/components/ui/OtpInput.vue'
import FloatingInput from '@/components/ui/FloatingInput.vue'
import FloatingSelect from '@/components/ui/FloatingSelect.vue'
import GoogleAuthButton from '@/components/ui/GoogleAuthButton.vue'
import { TURNSTILE_SITE_KEY } from '@/Utils/captcha'
import { redirectAfterAuth } from '@/Utils/authRedirect'
import { submitSignupToHubspot } from '@/Utils/hubspot'
import { resolveAttribution } from '@/Utils/attribution'
import { track, EVENTS } from '@/lib/tracking'
import { isPlausiblePhone } from '@/lib/phonePlausibility'

const router = useRouter()
const { notify } = useNotification()

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

// value → label mirrors the marketing-site sign-up org-type options.
const ORG_TYPES = [
	{ value: '', text: 'Select organization type' },
	{ value: '4', text: 'Corporate' },
	{ value: '2', text: 'Faith-based organization' },
	{ value: '3', text: 'Government' },
	{ value: '5', text: 'Video Production Company' },
	{ value: '6', text: 'Radio & TV' },
	{ value: '7', text: 'Entertainment' },
	{ value: '9', text: 'Education' },
	{ value: '8', text: 'Nonprofit' },
	{ value: '1', text: 'Other' },
]

const telOptions = {
	showDialCodeInList: true,
	showDialCodeInSelection: true,
	showFlags: true,
	showSearchBox: true,
}

// The bundled country list appends native names, e.g. "Germany (Deutschland)".
// Strip the trailing parenthetical so the dropdown shows English names only.
// English disambiguators survive because they're never the last group
// (e.g. "Congo (DRC) (Jamhuri ...)" becomes "Congo (DRC)").
function stripNativeName(name) {
	const trimmed = name.trim()
	if (!trimmed.endsWith(')')) return trimmed
	let depth = 0
	for (let i = trimmed.length - 1; i >= 0; i--) {
		if (trimmed[i] === ')') depth++
		else if (trimmed[i] === '(') {
			depth--
			if (depth === 0) return trimmed.slice(0, i).trim()
		}
	}
	return trimmed
}

let englishCountries
try {
	englishCountries = VueTelInput.props.allCountries
		.default()
		.map((c) => ({ ...c, name: stripNativeName(c.name) }))
} catch (e) {
	// Fall back to the component's built-in list (with native names).
	englishCountries = undefined
}

// Pin the countries Castr customers most commonly stream from to the top.
const preferredCountries = [
	'US',
	'CA',
	'GB',
	'DE',
	'AU',
	'NL',
	'FR',
	'IN',
	'BR',
	'AE',
]

// Turnstile bot protection. Enabled in production only: the site key is
// scoped to the production domain, so localhost/staging can't pass it.
const captchaEnabled = process.env.VUE_APP_API_ENV === 'prod'

const step = ref('form') // 'form' | 'code'
const processing = ref(false)
const error = ref('')

const form = ref({
	name: '',
	email: '',
	org_name: '',
	org_type: '',
})
const phoneInput = ref('')
const phoneNumber = ref('')
const phoneValid = ref(false)
// the number castr refused (taken / unusable), so re-validating it keeps the error
const refusedPhone = ref('')
const captchaToken = ref('')
const code = ref('')

const turnstile = ref(null)
const codeInput = ref(null)
const nameInput = ref(null)
const emailField = ref(null)
const orgInput = ref(null)
const fieldErrors = ref({
	name: '',
	email: '',
	org_name: '',
	org_type: '',
	phone: '',
})
const resendCountdown = ref(0)
const verifySuccess = ref(false)
const showSpamHint = ref(false)
let resendTimer = null
let spamHintTimer = null

// Mask for shoulder-surfing privacy: "govinda@castr.com" → "g•••••@castr.com"
const maskedEmail = computed(() => {
	const value = (form.value.email || '').trim()
	const at = value.indexOf('@')
	if (at < 1) return value
	return `${value[0]}•••••${value.slice(at)}`
})

const emailValid = computed(() => EMAIL_RE.test((form.value.email || '').trim()))
const codeValid = computed(() => /^\d{6}$/.test(code.value))

function onPhoneValidate(mobile) {
	phoneValid.value = !!mobile?.valid && isPlausiblePhone(mobile.number)
	if (phoneValid.value) {
		phoneNumber.value = mobile.number
		// the input re-validates on mount (back from the code step): castr's
		// refusal stays until the number actually changes
		if (mobile.number !== refusedPhone.value) fieldErrors.value.phone = ''
	}
}

function clearFieldError(field) {
	fieldErrors.value[field] = ''
}

// Enabled-button pattern: validate on submit and point at exactly what's
// missing instead of silently disabling the CTA.
function validateForm() {
	fieldErrors.value = {
		name: form.value.name.trim() ? '' : 'Please enter your name.',
		email: emailValid.value ? '' : 'Please enter a valid email.',
		org_name: form.value.org_name.trim()
			? ''
			: 'Please enter your organization name.',
		org_type: form.value.org_type ? '' : 'Please select an organization type.',
		phone: phoneValid.value ? '' : 'Enter a valid phone number.',
	}
	const errors = fieldErrors.value
	if (errors.name) nameInput.value?.focus()
	else if (errors.email) emailField.value?.focus()
	else if (errors.org_name) orgInput.value?.focus()
	return !Object.values(errors).some(Boolean)
}
// castr names the form field a refusal is about (`data: { field, reason }`):
// a phone already on another account, a rejected email. Those belong next to
// the field, never under the code cells.
const FIELD_ERROR_COPY = {
	phone_taken: 'That number is already on another account. Use a different number to continue.',
	invalid_phone: 'Enter a valid phone number.',
	phone_check_failed: 'We could not check this phone number. Please try again in a moment.',
	email_taken: 'An account with this email already exists. Log in instead.',
	email_rejected: 'We can\'t accept this email address. Please use your work email.',
}

/** Put a field refusal on its field. @return {boolean} whether it was one */
function applyFieldError(err) {
	const field = err?.data?.field
	if (field !== 'phone' && field !== 'email') return false
	fieldErrors.value[field] = FIELD_ERROR_COPY[err.data.reason] || err.message || 'Please check this field.'
	if (field === 'phone') refusedPhone.value = phoneNumber.value
	return true
}

/** A field refusal after the code step: back to the form, the field marked. */
function backToFormWith(err) {
	if (!applyFieldError(err)) return false
	step.value = 'form'
	code.value = ''
	error.value = ''
	verifySuccess.value = false
	nextTick(() => { if (err.data.field === 'email') emailField.value?.focus() })
	return true
}

function onCaptchaVerified(token) {
	captchaToken.value = token
}
function onCaptchaExpired() {
	captchaToken.value = ''
}
function startResendCountdown() {
	resendCountdown.value = 60
	clearInterval(resendTimer)
	resendTimer = setInterval(() => {
		resendCountdown.value -= 1
		if (resendCountdown.value <= 0) clearInterval(resendTimer)
	}, 1000)
}

// Typing a fresh digit dismisses the previous "wrong code" error.
watch(code, (value) => {
	if (value && error.value) error.value = ''
})

// Per-step page title + delayed "check spam" nudge on the code step.
watch(
	step,
	(value) => {
		document.title =
			value === 'code' ? 'Check your email – Castr' : 'Sign up – Castr'
		clearTimeout(spamHintTimer)
		showSpamHint.value = false
		if (value === 'code') {
			spamHintTimer = setTimeout(() => {
				showSpamHint.value = true
			}, 20000)
		}
	},
	{ immediate: true },
)

onBeforeUnmount(() => {
	clearInterval(resendTimer)
	clearTimeout(spamHintTimer)
})

function buildUserPayload() {
	const attribution = resolveAttribution()
	return {
		name: form.value.name.trim(),
		email: form.value.email.trim().toLowerCase(),
		phone: phoneNumber.value,
		org_name: form.value.org_name.trim(),
		org_type: form.value.org_type,
		captchaResponse: captchaToken.value,
		referral: attribution,
		referralType: attribution ? 'paid_ads' : '',
	}
}

async function requestCode() {
	if (processing.value) return
	error.value = ''
	if (!validateForm()) return
	if (captchaEnabled && !captchaToken.value) {
		error.value = 'Please complete the security check.'
		return
	}
	processing.value = true
	try {
		await UserService.requestSignupCode(buildUserPayload())
		track(EVENTS.SIGNUP_CODE_REQUESTED, {
			org_type: form.value.org_type,
			has_phone: phoneValid.value,
			is_resend: false,
		})
		step.value = 'code'
		// Clear the loading state BEFORE focusing: the OTP cells are disabled
		// while `processing` is true (via the `verifying` prop), and a disabled
		// input can't receive focus. Focusing must happen after this flips false
		// so the field is ready to type without an extra click.
		processing.value = false
		startResendCountdown()
		await nextTick()
		codeInput.value?.focus()
	} catch (err) {
		if (applyFieldError(err)) {
			if (err.data.field === 'email') emailField.value?.focus()
		} else {
			error.value = err?.message || 'Something went wrong. Please try again.'
		}
		turnstile.value?.reset()
		captchaToken.value = ''
		processing.value = false
	}
}

async function resendCode() {
	if (resendCountdown.value > 0 || processing.value) return
	error.value = ''
	processing.value = true
	try {
		await UserService.requestSignupCode(buildUserPayload())
		track(EVENTS.SIGNUP_CODE_REQUESTED, { is_resend: true })
		startResendCountdown()
		notify({ group: 'success', text: 'A new code is on its way.' })
	} catch (err) {
		if (!backToFormWith(err)) error.value = err?.message || 'Could not resend the code.'
	}
	processing.value = false
}

function editDetails() {
	step.value = 'form'
	code.value = ''
	error.value = ''
	verifySuccess.value = false
}

async function verifyCode() {
	if (processing.value || verifySuccess.value) return
	error.value = ''
	if (!codeValid.value) {
		error.value = 'Please enter the 6-digit verification code.'
		return
	}
	processing.value = true
	try {
		const { user, authKey } = await UserService.verifySignupCode(
			form.value.email.trim(),
			code.value,
		)
		UserService.setSession(user, authKey)
		track(EVENTS.SIGNUP_VERIFIED, {
			org_type: form.value.org_type,
			has_team_invite: !!router.currentRoute.value.query.join,
		})
		// Mirror the legacy WP signup: fire-and-forget the new signup to HubSpot.
		// Not awaited so it never delays or blocks the redirect into the app.
		submitSignupToHubspot({
			email: user?.email || form.value.email.trim(),
			name: user?.name || form.value.name.trim(),
			phone: phoneNumber.value,
		})
		// Flash the cells green, then redirect into the app.
		verifySuccess.value = true
		processing.value = false
		notify({ group: 'success', text: 'Welcome to Castr!' })
		setTimeout(() => redirectAfterAuth({ signup: true }), 500)
	} catch (err) {
		processing.value = false
		// The details are checked before the code is sent, but the phone can
		// still be taken by another signup while the code sits in the inbox.
		if (backToFormWith(err)) return
		const message = err?.message || 'An error occurred. Please try again.'
		error.value = message
		// Only a rejected code earns a cleared field; anything else leaves the
		// digits so it does not read as "wrong code".
		if (/invalid or expired code/i.test(message)) {
			codeInput.value?.shake()
			codeInput.value?.clear()
		}
	}
}

onMounted(() => {
	const { query } = router.currentRoute.value
	if (query?.name) form.value.name = query.name
	if (query?.email || query?.signup_email) {
		form.value.email = query.email || query.signup_email
	}
	nameInput.value?.focus()
})
</script>

<template>
	<div class="auth-page">
		<div class="auth-card">
			<div class="flex flex-col items-center text-center mb-7">
				<div class="auth-logo-wrapper">
					<div class="auth-logo-dot"></div>
					<a href="https://castr.com" aria-label="Go to Castr home">
						<img class="auth-logo" src="@/assets/castr.svg" alt="Castr" />
					</a>
				</div>
			</div>

			<!-- Step 1: details + captcha -->
			<template v-if="step === 'form'">
				<div class="mb-6">
					<h1 class="auth-title">Create your account</h1>
					<p class="auth-subtitle">Start your trial — no credit card required</p>
				</div>

				<GoogleAuthButton context="signup" />

				<Alert v-if="error" type="error" :text="error" class="left inline mb-4" />

				<form @submit.prevent="requestCode">
					<div class="mb-4">
						<FloatingInput
							ref="nameInput"
							id="su-name"
							label="Your name"
							v-model="form.name"
							autocomplete="name"
							:has-error="!!fieldErrors.name"
							@update:modelValue="clearFieldError('name')"
						/>
						<p v-if="fieldErrors.name" class="auth-error">{{ fieldErrors.name }}</p>
					</div>

					<div class="mb-4">
						<FloatingInput
							ref="emailField"
							id="su-email"
							label="Work email"
							hint="you@company.com"
							v-model="form.email"
							type="email"
							inputmode="email"
							autocomplete="email"
							:has-error="!!fieldErrors.email"
							@update:modelValue="clearFieldError('email')"
						/>
						<p v-if="fieldErrors.email" class="auth-error">{{ fieldErrors.email }}</p>
					</div>

					<div class="mb-4">
						<FloatingInput
							ref="orgInput"
							id="su-org"
							label="Organization name"
							v-model="form.org_name"
							autocomplete="organization"
							:has-error="!!fieldErrors.org_name"
							@update:modelValue="clearFieldError('org_name')"
						/>
						<p v-if="fieldErrors.org_name" class="auth-error">
							{{ fieldErrors.org_name }}
						</p>
					</div>

					<div class="mb-4">
						<FloatingSelect
							id="su-orgtype"
							label="Organization type"
							v-model="form.org_type"
							:options="ORG_TYPES"
							:has-error="!!fieldErrors.org_type"
							@change="clearFieldError('org_type')"
						/>
						<p v-if="fieldErrors.org_type" class="auth-error">
							{{ fieldErrors.org_type }}
						</p>
					</div>

					<div class="mb-4">
						<label class="ff-phone-label">Phone number</label>
						<vue-tel-input
							class="phone-input auth-phone"
							:class="{ 'auth-phone--error': !!fieldErrors.phone }"
							v-model="phoneInput"
							mode="international"
							auto-format
							:dropdown-options="telOptions"
							:preferred-countries="preferredCountries"
							:all-countries="englishCountries"
							:input-options="{ placeholder: '555 000 1234' }"
							@validate="onPhoneValidate"
						/>
						<p v-if="fieldErrors.phone" class="auth-error">{{ fieldErrors.phone }}</p>
					</div>

					<div
						v-if="captchaEnabled"
						class="flex justify-center mb-5 min-h-[65px]"
					>
						<TurnstileWidget
							ref="turnstile"
							:site-key="TURNSTILE_SITE_KEY"
							@verified="onCaptchaVerified"
							@expired="onCaptchaExpired"
							@error="onCaptchaExpired"
						/>
					</div>

					<div class="flex justify-center mt-1">
						<FormButton
							type="main"
							size="lg"
							class="!h-10 !px-12"
							:disabled="processing"
							@click="requestCode"
						>
							<template v-if="processing">
								Sending code
								<Spinner
									text=""
									classes="ml-2"
									spinner-color="currentColor"
									spinner-size="14px"
								/>
							</template>
							<template v-else>Start your trial</template>
						</FormButton>
					</div>

					<p class="auth-reassurance">
						<span>✓ No credit card required</span>
						<span>✓ Cancel anytime</span>
					</p>
				</form>

				<p class="auth-footer">
					Already have an account?
					<router-link to="/signin" class="auth-link">Log in</router-link>
				</p>
			</template>

			<!-- Step 2: verification code -->
			<template v-else>
				<div class="mb-6">
					<h1 class="auth-title">Check your email</h1>
					<p class="auth-subtitle">
						We sent a 6-digit code to
						<span class="text-surface-text-1 font-medium">{{ maskedEmail }}</span>.
						It expires in 15 minutes.
					</p>
				</div>

				<form @submit.prevent="verifyCode">
					<OtpInput
						ref="codeInput"
						v-model="code"
						:has-error="!!error"
						:verifying="processing"
						:success="verifySuccess"
						@complete="verifyCode"
					/>
					<p v-if="error" class="auth-error text-center mt-3">{{ error }}</p>

					<div class="flex justify-center mt-6">
						<FormButton
							type="main"
							size="lg"
							class="!h-10 !px-12"
							:disabled="!codeValid || processing || verifySuccess"
							@click="verifyCode"
						>
							<template v-if="processing">
								Verifying
								<Spinner
									text=""
									classes="ml-2"
									spinner-color="currentColor"
									spinner-size="14px"
								/>
							</template>
							<template v-else-if="verifySuccess">Success</template>
							<template v-else>Verify and continue</template>
						</FormButton>
					</div>

					<p class="auth-resend mt-6">
						<span>Didn't get the code?</span>
						<span v-if="resendCountdown > 0" class="auth-resend__wait">
							Resend in {{ resendCountdown }}s
						</span>
						<FormButton
							v-else
							custom-classes="auth-link font-medium"
							@click="resendCode"
						>
							Resend code
						</FormButton>
					</p>

					<transition name="fade">
						<p v-if="showSpamHint" class="auth-spam-hint mt-2">
							Can't find it? Check your spam folder.
						</p>
					</transition>

					<p class="text-center mt-2">
						<FormButton custom-classes="auth-ghost-btn text-tiny" @click="editDetails">
							← Edit details
						</FormButton>
					</p>
				</form>
			</template>
		</div>

		<p class="auth-social-proof">
			Trusted by hundreds of media and broadcast companies worldwide
		</p>

		<p class="auth-legal">
			<template v-if="captchaEnabled">Protected by Cloudflare. </template>By continuing you agree to Castr's
			<a href="https://castr.com/terms" target="_blank" rel="noopener">Terms</a>
			and
			<a href="https://castr.com/privacypolicy" target="_blank" rel="noopener"
				>Privacy Policy</a
			>.
		</p>
	</div>
</template>

<style scoped>
.auth-page {
	position: relative;
	width: 100%;
	min-height: 100vh;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: 40px 16px;
	gap: 18px;
}
/* Ambient brand wash. Sized in percentages and anchored top-centre to match
   the marketing pages (ToolShell, PricingPage); a fixed-pixel circle shrinks
   to a blob on wide displays. Second layer lifts the card off the flat page. */
.auth-page::before {
	content: '';
	position: absolute;
	inset: 0;
	background:
		radial-gradient(65% 45% at 50% 0%, rgba(0, 98, 255, 0.16), transparent 68%),
		radial-gradient(34% 42% at 50% 46%, rgba(0, 98, 255, 0.07), transparent 72%);
	pointer-events: none;
}
.auth-page > * {
	position: relative;
}
.auth-card {
	width: 100%;
	max-width: 408px;
	background-color: var(--castr-surface);
	border: 1px solid var(--castr-border);
	border-radius: 16px;
	box-shadow: 0 24px 60px -24px rgba(0, 0, 0, 0.9);
	padding: 40px 36px 32px;
}
/* Native 120x40 rendering + dot copied verbatim from MainSidebarLogo.vue
   (top-3 left-[116px] size-1) so it matches the dashboard exactly. */
.auth-logo {
	width: 120px;
	height: 40px;
}
.auth-logo-wrapper {
	position: relative;
	display: inline-block;
	line-height: 0;
}
.auth-logo-dot {
	position: absolute;
	width: 4px;
	height: 4px;
	border-radius: 50%;
	left: 116px;
	top: 12px;
	background-color: #ef0000;
	animation: blink 2s ease-in-out infinite;
}
.auth-title {
	font-family: var(--font-display);
	font-size: 24px;
	line-height: 1.15;
	font-weight: 700;
	letter-spacing: -0.02em;
	color: #fff;
	text-align: center;
	margin-bottom: 6px;
}
.auth-subtitle {
	font-size: 14px;
	color: var(--castr-text-muted);
	text-align: center;
}
.ff-phone-label {
	display: block;
	font-weight: 500;
	font-size: 11px;
	letter-spacing: 0.02em;
	color: var(--castr-text-muted);
	margin-bottom: 6px;
	margin-left: 2px;
}
.auth-reassurance {
	margin-top: 14px;
	display: flex;
	justify-content: center;
	gap: 14px;
	font-size: 12px;
	color: var(--castr-text-muted);
}
.auth-social-proof {
	max-width: 408px;
	text-align: center;
	font-size: 12px;
	font-weight: 500;
	color: var(--castr-text-muted);
	margin-bottom: -8px;
}
.auth-error {
	margin-top: 6px;
	font-size: 12px;
	color: var(--castr-danger);
}
.auth-footer {
	margin-top: 24px;
	text-align: center;
	font-size: 14px;
	color: var(--castr-text-muted);
}
.auth-link {
	color: var(--castr-blue-soft);
	font-weight: 600;
}
.auth-link:hover {
	text-decoration: underline;
}
.auth-ghost-btn {
	color: var(--castr-text-muted);
	background: none;
	border: none;
	padding: 0;
}
.auth-ghost-btn:hover {
	color: var(--castr-text);
}
.auth-resend {
	text-align: center;
	font-size: 13px;
	color: var(--castr-text-muted);
}
.auth-resend button {
	background: none;
	border: none;
	padding: 0;
	font-size: 13px;
}
.auth-resend__wait {
	color: var(--castr-text-muted);
	font-variant-numeric: tabular-nums;
}
.auth-spam-hint {
	text-align: center;
	font-size: 12px;
	color: var(--castr-text-muted);
}
.fade-enter-active,
.fade-leave-active {
	transition: opacity 0.4s ease;
}
.fade-enter-from,
.fade-leave-to {
	opacity: 0;
}
.auth-legal {
	max-width: 408px;
	text-align: center;
	font-size: 11px;
	line-height: 1.5;
	color: var(--castr-text-muted);
	padding: 0 12px;
}
.auth-legal a {
	color: var(--castr-text-muted);
	text-decoration: underline;
}
.auth-legal a:hover {
	color: var(--castr-text);
}
/* Primary action, matched to the marketing hero CTA. The radius needs
   !important because FormButton hardcodes `!rounded-md`. */
.auth-card :deep(button.form-button) {
	font-size: 14px;
	font-weight: 600;
	letter-spacing: 0.01em;
	border-radius: 12px !important;
}
.auth-card :deep(button.bg-main:not(:disabled)) {
	box-shadow: 0 10px 30px -8px rgba(0, 98, 255, 0.5);
	transition:
		box-shadow 0.2s ease,
		transform 0.12s ease,
		background-color 0.15s ease;
}
.auth-card :deep(button.bg-main:not(:disabled):hover) {
	transform: translateY(-1px);
	box-shadow: 0 14px 36px -8px rgba(0, 98, 255, 0.6);
}
.auth-card :deep(button.bg-main:not(:disabled):active) {
	transform: translateY(0);
	box-shadow: 0 6px 18px -8px rgba(0, 98, 255, 0.5);
}
.auth-card :deep(button:disabled) {
	background-color: rgba(255, 255, 255, 0.06) !important;
	color: var(--castr-text-muted) !important;
	cursor: not-allowed;
	box-shadow: none;
}
</style>

<style>
/* The auth routes are fullScreen, but #app and the always-mounted AppFooter
   still paint the navy app shell behind the card. Scoped to :has() so the
   override lifts automatically on route change. */
body:has(.auth-page),
body:has(.auth-page) #app {
	background-color: var(--castr-bg);
}
body:has(.auth-page) .footer span {
	color: var(--castr-text-muted);
}

/* Align the vue-tel-input control with the marketing form fields. */
.auth-phone.vue-tel-input {
	border-radius: 12px;
	border: 1px solid var(--castr-border);
	background-color: var(--castr-field);
	height: 46px;
	box-shadow: none;
	transition:
		border-color 0.15s ease,
		box-shadow 0.15s ease;
}
.auth-phone.vue-tel-input:hover:not(:focus-within) {
	border-color: var(--castr-border-strong);
}
.auth-phone.vue-tel-input:focus-within {
	border-color: var(--castr-blue);
	box-shadow: 0 0 0 3px var(--castr-focus-ring);
}
.auth-phone--error.vue-tel-input {
	border-color: var(--castr-danger);
}
/* Transparent so the wrapper's translucent fill is not layered twice. */
.auth-phone .vti__input {
	background-color: transparent !important;
	color: var(--castr-text);
	border: none;
	border-radius: 0 12px 12px 0;
	font-size: 15px;
	padding-left: 12px;
}
.auth-phone .vti__input::placeholder {
	color: var(--castr-text-muted);
}
.auth-phone .vti__dropdown {
	background-color: transparent;
	color: var(--castr-text);
	border-radius: 12px 0 0 12px;
	padding: 0 10px 0 12px;
	border-right: 1px solid var(--castr-border);
}
.auth-phone .vti__dropdown:hover,
.auth-phone .vti__dropdown.open {
	background-color: rgba(255, 255, 255, 0.08);
}
.auth-phone .vti__dropdown-arrow {
	color: var(--castr-text-muted);
}
/* Country dropdown panel */
.auth-phone .vti__dropdown-list {
	background-color: var(--castr-field-solid);
	border: 1px solid var(--castr-border);
	border-radius: 12px;
	box-shadow: 0 16px 36px -10px rgba(0, 0, 0, 0.8);
	width: 340px;
	max-width: 80vw;
	z-index: 30;
}
.auth-phone .vti__dropdown-list.below {
	top: 46px;
}
/* Search box */
.auth-phone .vti__search_box {
	width: calc(100% - 16px);
	margin: 8px;
	padding: 9px 12px;
	background-color: rgba(255, 255, 255, 0.06);
	border: 1px solid var(--castr-border);
	border-radius: 8px;
	color: var(--castr-text);
	font-size: 14px;
	outline: none;
}
.auth-phone .vti__search_box:focus {
	border-color: var(--castr-blue);
	box-shadow: 0 0 0 3px var(--castr-focus-ring);
}
.auth-phone .vti__search_box::placeholder {
	color: var(--castr-text-muted);
}
/* Country rows */
.auth-phone .vti__dropdown-item {
	padding: 8px 12px;
	font-size: 14px;
	color: var(--castr-text);
}
.auth-phone .vti__dropdown-item.highlighted {
	background-color: rgba(255, 255, 255, 0.08);
}
.auth-phone .vti__dropdown-item .vti__dropdown-arrow {
	color: var(--castr-text-muted);
}
.auth-phone .vti__dropdown-item strong {
	font-weight: 600;
}
</style>
